/**
 * ConversationNotificationItem — per-thread wrapper that owns local
 * reply state and composes the `Notification` primitive at
 * variant="conversation".
 *
 * Headless: no `browser.*`, no `chrome.*`, no `theme.ts`, no storage
 * hooks. Reply transport, dismiss transport, and i18n labels are all
 * injected by the host. Each app supplies its own thin wrapper:
 * the extension wires reply/dismiss to `browser.runtime.sendMessage`;
 * the web wires them to `fetch`.
 *
 * Reply FSM is delegated to the shared `useReplyState` hook (same
 * one `NotificationListItem` uses): handles text + sending/sent/error
 * status, auto-clear timers, unmount cleanup, and the `stateRef`
 * mirror that keeps callbacks stable across storage-driven re-renders.
 * This wrapper just picks the right `(msgId, actionIndex)` from the
 * thread, builds the submit closure, and assembles the `replySlot`
 * region from `<ReplyComposer>` + status spans.
 */

import type { ConversationThread } from "@reflecto/client-core";
import { useCallback, useMemo } from "react";
import {
  base64ImageDataUrl,
  formatRelativeTime,
  type RelativeTimeLabels,
} from "../../utils/format";
import { Notification } from "../Notification";
import { type ReplyResult, useReplyState } from "../NotificationListItem/useReplyState";
import { ReplyComposer } from "../ReplyComposer";
import styles from "./styles.module.css";

/**
 * i18n bag for ConversationNotificationItem. `unreadAriaLabel` is a
 * function because the unread count varies per render — the host
 * formats `"3 unread messages"` (or its locale equivalent) given the
 * count.
 */
export interface ConversationNotificationItemLabels {
  /**
   * Text for the relative timestamp on this card. Required: it was English-only
   * inside `formatRelativeTime` until desktop slice 5 (R1-f), which is how
   * "Zuletzt verwendet just now" reached German users.
   */
  relativeTime: RelativeTimeLabels;
  replyAriaLabel: string;
  replyPlaceholder: string;
  /** ARIA label for the reply composer's send button. */
  sendLabel: string;
  sent: string;
  failedToSend: string;
  /** Called with the (positive) unread count to format SR text. */
  unreadAriaLabel: (count: number) => string;
  dismiss: string;
  /** SR label on the messages-list region the conversation reveals
   *  when expanded. */
  messagesListLabel: string;
  /** Author label for messages sent by the device owner (i.e. the
   *  user's outgoing replies). Typically "You". */
  deviceOwner: string;
}

export interface ConversationNotificationItemProps {
  thread: ConversationThread;
  /** Base64-encoded app icon string (without the data-URL prefix). */
  appIcon?: string;
  /**
   * Called when the user dismisses this conversation. When omitted
   * the close button is not rendered (same contract as
   * `Notification.onClose`).
   */
  onDismiss?: (key: string) => void;
  /**
   * Reply submit callback — receives the latest message's `msgId`,
   * the reply action's `actionIndex`, and the trimmed text. Promise
   * result drives the inline sent/error feedback. When omitted, the
   * ReplyComposer is hidden.
   */
  onSendReply?: (msgId: string, actionIndex: number, text: string) => Promise<ReplyResult>;
  /**
   * When true, the thread's reply composer is withheld. Mirrors the prop of the same name on
   * `NotificationListItem` — a thread reply is the same reply, so leaving it ungated here would
   * just move the dead composer into the grouped view.
   */
  interactionsUnavailable?: boolean;
  /** When true, apply left-indented styling (used inside app groups in
   *  the side panel). */
  indent?: boolean;
  /**
   * Visual-only exit flag. Mirrors `NotificationListItem.dismissing`:
   * the list flips this on for ~180ms so the thread card fades + slides
   * out before the data is dropped from the host's storage. See that
   * prop's JSDoc for the rationale.
   */
  dismissing?: boolean;
  labels: ConversationNotificationItemLabels;
}

export function ConversationNotificationItem({
  thread,
  appIcon,
  onDismiss,
  onSendReply,
  interactionsUnavailable,
  indent = false,
  dismissing,
  labels,
}: ConversationNotificationItemProps) {
  // Reply action: only the latest message's actions are valid on Android.
  const replyAction = thread.actions?.find((a) => !a.actionType || a.actionType === "reply");
  const latestMsg = thread.messages[thread.messages.length - 1];
  // Capture primitives for the memo deps. `replyAction` is a fresh
  // object on every storage-driven re-render (thread is re-deserialised
  // from storage), so depending on it directly busts the memo cache
  // every cycle. The actionIndex + msgId strings are stable for the
  // life of the thread.
  const replyActionIndex = replyAction?.actionIndex;
  const latestMsgId = latestMsg?.msgId;
  const canReply = Boolean(
    replyActionIndex !== undefined && latestMsgId && onSendReply && !interactionsUnavailable
  );

  const submit = useMemo(
    () => async (text: string) => {
      if (!onSendReply || replyActionIndex === undefined || !latestMsgId) {
        return { success: false };
      }
      return onSendReply(latestMsgId, replyActionIndex, text);
    },
    [onSendReply, replyActionIndex, latestMsgId]
  );

  const reply = useReplyState({
    submit,
    canReply,
    fallbackErrorLabel: labels.failedToSend,
  });

  // Assemble reply slot: ReplyComposer + inline sent/error feedback.
  const replySlot = canReply ? (
    <>
      <ReplyComposer
        label={labels.replyAriaLabel}
        placeholder={labels.replyPlaceholder}
        sendLabel={labels.sendLabel}
        value={reply.text}
        onChange={reply.setText}
        onSend={reply.send}
        sending={reply.status === "sending"}
      />
      {reply.status === "sent" && (
        <span role="status" className={styles.replyFeedbackOk}>
          {labels.sent}
        </span>
      )}
      {reply.error && (
        <span role="status" className={styles.replyFeedbackErr}>
          {reply.error}
        </span>
      )}
    </>
  ) : undefined;

  const previewText =
    thread.isGroup && latestMsg?.sender
      ? `${latestMsg.sender}: ${latestMsg.text}`
      : (latestMsg?.text ?? "");

  const appIconUrl = appIcon ? base64ImageDataUrl(appIcon) : undefined;

  // Memo the messages array so changing only thread metadata (e.g.
  // unreadCount after markRead) doesn't re-create a new array
  // identity that would trip downstream memos in Notification.
  const primitiveMessages = useMemo(
    () =>
      thread.messages.map((m) => ({
        id: m.id,
        sender: m.sender,
        text: m.text,
        timestamp: m.timestamp,
      })),
    [thread.messages]
  );

  // Stable dismiss handler — keyed on thread.key so the closure
  // identity doesn't change when only thread message data updates.
  const handleClose = useCallback(() => onDismiss?.(thread.key), [onDismiss, thread.key]);

  const card = (
    <Notification
      variant="conversation"
      appName={thread.app}
      appIconUrl={appIconUrl}
      timestamp={formatRelativeTime(thread.lastMessageTimestamp, labels.relativeTime)}
      title={thread.title}
      body={previewText}
      unread={thread.unreadCount > 0}
      unreadLabel={labels.unreadAriaLabel(thread.unreadCount)}
      messages={primitiveMessages}
      deviceOwnerLabel={labels.deviceOwner}
      messagesListLabel={labels.messagesListLabel}
      onClose={onDismiss ? handleClose : undefined}
      closeLabel={labels.dismiss}
      replySlot={replySlot}
      className={dismissing ? styles.dismissing : undefined}
      // KbdRow wrapper owns aria-hidden during dismiss; don't double it here.
      data-conv-key={thread.key}
    />
  );

  return indent ? <div className={styles.indent}>{card}</div> : card;
}
